import {ModelRepository, RequestMethod} from 'b302-frontend-library';
import University from '../models/University';
import Country from '../models/Country';
import FormQuestion from '../models/FormQuestion';
import Subject from '../models/Subject';
import {UniversityIndexRequest, UniversitySearchRequest, UniversitySubjectsRequest} from '../requests/University';
import {PaginationRequest} from '../requests/Util';

export interface UniversityFormRequest {
    name?: string;
    city?: string;
    rating?: string;
    description?: string;
    website?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    country?: number;
    longitude?: string;
    latitude?: string;
}

export interface FilterParams {
    countries?: number[];
    min_cost?: number;
    max_cost?: number;
    rating?: number;
    searchQuery?: string;
    favorites?: boolean;
}

export interface SubjectFilterParams extends PaginationRequest{
    search?: string;
    order_by?: string;
    direction?: string;
}

export interface UniversityFilterResponse {
    countries: Country[];
    universities: University[];
}

export interface UniversityPart {
    id: number;
    name: string;
    active_questions: FormQuestion[];
}

export enum Part {
    PROGRAM = 'academic-program', SUBJECTS = 'subjects', SERVICES = 'services', EXPERIENCES = 'experiences', GENERAL = 'general'
}

class UniversityRepository extends ModelRepository<University> {

    public constructor() {
        super('/universities');
    }

    public filter(params: UniversityIndexRequest) {
        return this.requestWithOptions<UniversityFilterResponse>(this.url(), RequestMethod.GET, {
            params: { ...params, favorites: params?.favorites ? 1 : undefined }
        }).sendPagination();
    }

    public create(request: UniversityFormRequest) {
        return this.request<University>(this.url(), RequestMethod.POST, request).send();
    }

    public update(university: number, request: UniversityFormRequest) { // TODO: maybe use transformer instead of custom update
        return this.request<University>(this.eUrl(university), RequestMethod.PUT, request).send();
    }

    public search(request: UniversitySearchRequest) {
        return this.requestWithOptions<University[]>(this.url('/search'), RequestMethod.GET, {
            params: { ...request }
        }).sendPagination();
    }

    public uploadImages(university: number, data: FormData) {
        return this.request<University>(this.eUrl(university, '/upload-images'), RequestMethod.POST, data).send();
    }

    public deleteImages(university: number, images: number[]) {
        return this.request<University>(this.eUrl(university, '/delete-images'), RequestMethod.POST, { images }).send();
    }

    public withParts(university: number, part: Part) {
        return this.requestWithOptions<UniversityPart[]>(this.eUrl(university, '/with-parts'), RequestMethod.GET, {
            params: { part }
        }).send();
    }

    public withCosts(university: number, part: Part) {
        return this.requestWithOptions<University>(this.eUrl(university, '/with-parts'), RequestMethod.GET, {
            params: { part }
        }).send();
    }

    public subjects(university: number, params?: UniversitySubjectsRequest) {
        return this.requestWithOptions<Subject[]>(this.eUrl(university, '/with-parts'), RequestMethod.GET, {
            params: { part: Part.SUBJECTS, ...params }
        }).sendPagination();
    }
}

export default UniversityRepository;
